import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { GovernanceListingSection } from "../../components/governance/GovernanceListingSection";
import { ComplianceCenterMetricTile } from "../../components/compliance-center/ComplianceCenterMetricTile";
import { ComplianceCenterListRow } from "../../components/compliance-center/ComplianceCenterListRow";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

type LifecycleData = {
  lifecycle?: Record<string, number>;
  controls?: Array<{ controlCode: string; standardCode?: string; status?: string }>;
  totals?: Record<string, number>;
};

export default function ComplianceCenterControlLifecycleWorkspace() {
  const { t } = useI18n();
  const { token } = useAuth();
  const [data, setData] = useState<LifecycleData>({});

  useEffect(() => {
    const load = async () => {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_CONTROL_LIFECYCLE, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json().catch(() => ({}));
      setData(json?.data || {});
    };
    void load();
  }, [token]);

  const lifecycle = data.lifecycle || {};
  const controls = Array.isArray(data.controls) ? data.controls : [];
  const totals = data.totals || {};

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t("complianceCenterUi.nav.controlLifecycle", "Control Lifecycle")}
        description={t(
          "complianceCenterUi.controlLifecycleIntro",
          "Operational lifecycle view for control implementation, remediation, and verification.",
        )}
      >
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid5}`}>
          <ComplianceCenterMetricTile label={t("complianceCenterUi.identified", "Identified")} value={lifecycle.identified ?? 0} tone="blue" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.assessed", "Assessed")} value={lifecycle.assessed ?? 0} tone="purple" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.inRemediation", "In remediation")} value={lifecycle.inRemediation ?? 0} tone="orange" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.verified", "Verified")} value={lifecycle.verified ?? 0} tone="green" />
          <ComplianceCenterMetricTile label={t("complianceCenterUi.blocked", "Blocked")} value={lifecycle.blocked ?? 0} tone="red" />
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection title={t("complianceCenterUi.recentControls", "Recent controls")}>
        <p className="text-xs text-slate-500 m-0 mb-3">
          {t("complianceCenterUi.lifecycleSources", "Sources")}: {t("complianceCenterUi.verifiedStates", "verified states")}{" "}
          {totals.verifiedStates ?? 0} · {t("complianceCenterUi.recommendations", "recommendations")} {totals.recommendations ?? 0} ·{" "}
          {t("complianceCenterUi.remediationTasks", "remediation tasks")} {totals.remediationTasks ?? 0}
        </p>
        <div className={listStyles.listStack}>
          {controls.slice(0, 20).map((control, idx) => (
            <ComplianceCenterListRow
              key={`${control.controlCode}-${idx}`}
              title={control.controlCode}
              meta={`${control.standardCode || "—"} · ${control.status || "unknown"}`}
            />
          ))}
          {!controls.length ? (
            <p className={listStyles.panelSubtitle}>{t("complianceCenterUi.noControls", "No controls returned.")}</p>
          ) : null}
        </div>
      </GovernanceListingSection>
    </div>
  );
}
